// Reusable collapsible panel — MUI sx, no CSS modules.
import { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import {
  resolveDefaultOpen,
  resolveEffectiveOpen,
  buildPanelTestId,
  handlePanelHeaderKeyDown,
  renderPanelContent,
  renderPanelHeader,
} from '@/components/ui/PanelHelpers';

/**
 * A reusable, generic collapsible panel component.
 *
 * @param {Object} props
 * @param {string} props.title - The display title of the panel.
 * @param {import('react').ReactNode} props.children - Content inside the panel when expanded.
 * @param {number} [props.count] - Optional count badge shown right-aligned in header.
 * @param {boolean} [props.defaultOpen=true] - Initial expanded/collapsed state (uncontrolled).
 * @param {boolean} [props.isOpen] - Alias for defaultOpen (backward compat).
 * @param {boolean} [props.open] - Controlled open state (overrides internal state).
 * @param {boolean} [props.forceOpen] - If true, overlays open visually without changing saved/internal state.
 * @param {Function} [props.onToggle] - Callback when toggled in controlled mode.
 * @param {boolean} [props.showEditIcon] - Whether to show the edit (pencil) icon in the header.
 * @param {Function} [props.onEditClick] - Callback when the edit icon is clicked.
 * @param {import('react').ReactNode} [props.headerActions] - Optional extra nodes in header action area.
 * @param {string} [props.testId] - Optional explicit test ID.
 * @param {boolean} [props.flat] - Flat content area (no padding, for divider-style task lists).
 * @param {boolean} [props.collapsible=true] - When false, content stays visible and the header is not clickable.
 * @param {number} [props.tabIndex] - When set, the panel root is the tab stop (header uses -1).
 * @param {number} [props.headerTabIndex] - Override header tabIndex (e.g. -1 to skip header in tab order).
 * @param {Function} [props.onFocus] - Focus handler on the panel root (for panel-level keyboard nav).
 * @param {Function} [props.onKeyDown] - Keydown on the panel root (in addition to header toggle keys).
 */
const Panel = ({
  title,
  children,
  count,
  defaultOpen: defaultOpenProp = true,
  isOpen: isOpenProp,
  open,
  forceOpen,
  onToggle,
  showEditIcon,
  onEditClick,
  headerActions,
  testId,
  contentClassName,
  flat = false,
  collapsible = true,
  hideHeader = false,
  tabIndex,
  headerTabIndex: headerTabIndexProp,
  onFocus,
  onKeyDown: onPanelKeyDown,
}) => {
  const [internalOpen, setInternalOpen] = useState(
    resolveDefaultOpen(isOpenProp, defaultOpenProp),
  );

  const isControlled = open !== undefined;
  const isOpen = resolveEffectiveOpen({
    collapsible,
    hideHeader,
    isControlled,
    open,
    internalOpen,
    forceOpen,
  });
  const finalTestId = buildPanelTestId(testId, title);
  const contentId = `${finalTestId}-content`;
  const hasCount = Number.isFinite(count);

  const toggle = useCallback(() => {
    if (onToggle) onToggle(!isOpen);
    if (!isControlled) setInternalOpen((o) => !o);
  }, [onToggle, isOpen, isControlled]);

  const handleEditClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (onEditClick) onEditClick();
    },
    [onEditClick],
  );

  const onHeaderKeyDown = useCallback(
    (e) => handlePanelHeaderKeyDown(e, toggle),
    [toggle],
  );

  const managesPanelTabStop = tabIndex !== undefined;
  const headerTabIndex =
    headerTabIndexProp ?? (managesPanelTabStop ? -1 : undefined);

  return (
    <Box
      data-testid={finalTestId}
      tabIndex={tabIndex}
      onFocus={onFocus}
      onKeyDown={onPanelKeyDown}
      sx={{
        border: hideHeader ? 0 : 1,
        borderColor: 'divider',
        borderRadius: hideHeader ? 0 : 1,
        bgcolor: hideHeader ? 'transparent' : 'background.paper',
        mb: hideHeader ? 0 : 1,
        ...(managesPanelTabStop &&
          tabIndex === 0 && {
            '&:focus-within': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }),
      }}
    >
      {renderPanelHeader({
        collapsible,
        isOpen,
        contentId,
        toggle,
        onKeyDown: onHeaderKeyDown,
        title,
        hasCount,
        count,
        headerActions,
        showEditIcon,
        onEditClick: handleEditClick,
        headerTabIndex,
      })}
      {renderPanelContent({
        collapsible: collapsible && !hideHeader,
        isOpen,
        testId: finalTestId,
        contentClassName,
        flat,
        children,
      })}
    </Box>
  );
};

export default Panel;
